import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { CaseDetail, Fundamental, InstrumentDetail, Money, PriceHistory, Quote,
              TradeIdea } from '../../api/client'
import type { AktivDetail, AktivZeile, AmpelKurz, FallKurz, NewsKurz }
  from '../../hooks/instrumentRegeln'
import { detailAusAmpel, detailAusFall, detailAusIdee, detailAusNews, kursStandAus,
         zeileAusAmpel, zeileAusFall, zeileAusIdee, zeileAusNews, zeitpunkt }
  from '../../hooks/instrumentRegeln'
import { Card } from '../card/Card'
import { DetailSection } from '../detail/DetailSection'
import { Field } from '../detail/FieldGrid'
import { EmptyState } from '../common/Feedback'
import { Geld, geldtext, kapitalisierung } from '../../pages/priceFormat'
import type { Zahlform } from '../../pages/priceFormat'
import { AktivitaetDetail } from './AktivitaetDetail'
import { AktivitaetKarte } from './AktivitaetKarte'
import { InstrumentKopf } from './InstrumentKopf'
import s from './InstrumentAnsicht.module.scss'

/** Was rechts neben den Stammdaten steht. Die Seite lädt es, die Vorlage stellt es. */
export interface InstrumentUmfeld {
  faelle: FallKurz[]; faelleGesamt: number
  news: NewsKurz[]; newsGesamt: number
  ampeln: AmpelKurz[]; ampelnGesamt: number
  ideen: TradeIdea[]; ideenGesamt: number
  /** Der jüngste Kurs je Handelsplatz, nach MIC. */
  kurse: Record<string, Quote>
  /** Die letzten Tage am Hauptplatz — für die Veränderung im Kopf. */
  balken: PriceHistory[]
  aufWatchlist: boolean | null
  /** Handelsplatzname nach MIC. */
  boersen: Record<string, string>
}

const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })
const laender = new Intl.DisplayNames(['de'], { type: 'region' })

function landZuCode(code: string): string | null {
  if (!code) return null
  try { const n = laender.of(code); return n && n !== code ? n : null } catch { return null }
}

/** 217128910848 → „217,13 Mrd" — gerechnet hier, die Schnittstelle liefert Zahlen. */
function betrag(wert: string | null): string | null {
  if (wert === null) return null
  const n = Number(wert)
  if (!Number.isFinite(n)) return null
  const stufen: [number, string][] = [[1e12, 'Bio'], [1e9, 'Mrd'], [1e6, 'Mio'], [1e3, 'Tsd']]
  for (const [teiler, einheit] of stufen) {
    if (Math.abs(n) >= teiler) {
      return `${(n / teiler).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${einheit}`
    }
  }
  return n.toLocaleString('de-DE', { maximumFractionDigits: 0 })
}

function anteil(wert: string | null): string | null {
  if (wert === null) return null
  const n = Number(wert)
  if (!Number.isFinite(n)) return null
  return `${(n * 100).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'exceptZero' })} %`
}

const alsBilanzbetrag: Zahlform = (wert, waehrung) => `${betrag(wert) ?? wert} ${waehrung}`

/** Ein Bilanzbetrag, gekürzt und mit Umrechnung; negativ in Rot. */
function Geldwert({ wert }: { wert: Money | null }) {
  if (!wert) return undefined
  return (
    <span className={Number(wert.amount) < 0 ? s.negativ : undefined}>
      <Geld wert={wert} schreibe={alsBilanzbetrag} />
    </span>
  )
}

function Anteil({ wert }: { wert: string | null }) {
  const text = anteil(wert)
  if (text === null) return undefined
  return <span className={Number(wert) < 0 ? s.negativ : undefined}>{text}</span>
}

/**
 * Die Instrumentseite — Stammdaten links, das Umfeld rechts.
 *
 * Links die Abschnitte Identifikation, Größe, Kennzahlen und Handelsplätze;
 * rechts, mitlaufend beim Scrollen, vier Karten mit dem, was zu diesem
 * Titel geschieht: Fälle, News, Einschätzungen, Ideen. Ein Klick auf eine
 * Zeile öffnet ihr Detail in der Schublade rechts, ohne die Seite zu
 * verlassen; der Sprung auf die Seite dahinter steht in der Schublade.
 *
 * Die Seite lädt, die Vorlage stellt — beide rendern DIESE Komponente.
 */
export function InstrumentAnsicht({ instrument, umfeld, back = '/instruments', laedt = false,
                                    fallDetail }: {
  instrument: InstrumentDetail
  umfeld: InstrumentUmfeld
  /** Wohin „Zur Liste" führt — mit den Filtern der Liste, wenn sie mitkamen. */
  back?: string
  laedt?: boolean
  /** Holt das Voll-Detail eines Falls für die Schublade. Ohne: nur die Zeile. */
  fallDetail?: (id: number, signal: AbortSignal) => Promise<CaseDetail | null>
}) {
  const [heute] = useState(() => new Date())
  const [offen, setOffen] = useState<AktivZeile | null>(null)
  const [fall, setFall] = useState<CaseDetail | null>(null)
  const [fallLaedt, setFallLaedt] = useState(false)

  const zuklappen = useCallback(() => setOffen(null), [])

  // Das Voll-Detail eines Falls kommt nach — Zusammenfassung und Verlauf
  // stehen nicht in der Liste.
  useEffect(() => {
    setFall(null)
    if (!offen || offen.art !== 'fall' || !fallDetail) return
    let aktuell = true
    const steuerung = new AbortController()
    setFallLaedt(true)
    fallDetail(offen.id, steuerung.signal)
      .then((d) => { if (aktuell) { setFall(d); setFallLaedt(false) } })
      .catch(() => { if (aktuell) setFallLaedt(false) })
    return () => { aktuell = false; steuerung.abort() }
  }, [offen, fallDetail])

  const zeilen = useMemo(() => ({
    faelle: umfeld.faelle.map((f) => zeileAusFall(f, heute)),
    news: umfeld.news.map((n) => zeileAusNews(n, heute)),
    ampeln: umfeld.ampeln.map((a) => zeileAusAmpel(a, heute)),
    ideen: umfeld.ideen.map((i) => zeileAusIdee(i, heute)),
  }), [umfeld, heute])

  const detail: AktivDetail | null = useMemo(() => {
    if (!offen) return null
    if (offen.art === 'fall') {
      const f = umfeld.faelle.find((x) => x.id === offen.id)
      return f ? detailAusFall(f, fall) : null
    }
    if (offen.art === 'news') {
      const n = umfeld.news.find((x) => x.id === offen.id)
      return n ? detailAusNews(n, instrument.isin) : null
    }
    if (offen.art === 'ampel') {
      const a = umfeld.ampeln.find((x) => x.id === offen.id)
      return a ? detailAusAmpel(a, instrument.isin) : null
    }
    const i = umfeld.ideen.find((x) => x.id === offen.id)
    return i ? detailAusIdee(i) : null
  }, [offen, fall, umfeld, instrument.isin])

  const hauptplatz = instrument.venues.find((v) => v.is_primary) ?? instrument.venues[0]
  const kurs = kursStandAus(umfeld.kurse[instrument.primary_exchange]
                            ?? Object.values(umfeld.kurse)[0] ?? null, umfeld.balken)
  const stand = instrument.fundamentals[0]
  const ref = encodeURIComponent(`isin:${instrument.isin}`)

  return (
    <div className={s.seite}>
      <Link to={back} className={s.back}>‹ Zur Liste</Link>
      <InstrumentKopf isin={instrument.isin} kuerzel={hauptplatz?.symbol ?? null}
                      branche={instrument.sector_name} land={instrument.country_code}
                      groesse={instrument.market_cap_class} marktkap={instrument.market_cap}
                      marktkapWaehrung={instrument.market_cap_ccy}
                      aufWatchlist={umfeld.aufWatchlist} kurs={kurs} />

      <div className={s.spalten}>
        <div className={s.links}>
          <DetailSection title="Identifikation" span={12} columns={2}>
            <Field label="Name" value={instrument.name} emphasis />
            <Field label="ISIN" value={instrument.isin} mono copy={instrument.isin}
                   help="Internationale Wertpapierkennnummer, zwölf Stellen. Eindeutig über alle Instrumente — ein Titel hat genau eine." />
            <Field label="ISIN-Land" mono
                   value={<Land code={instrument.country_code} />}
                   help="Die ersten beiden Zeichen der ISIN. Sie benennen die Stelle, die die Kennung vergeben hat — NICHT den Sitz des Unternehmens: Tencent steht unter KY (Kaimaninseln), das ADR von Sony unter US." />
            <Field label="Branche" value={instrument.sector_name ?? undefined}
                   help="Der GICS-Sektor im Klartext. Fehlt bei ETFs: Ein Fonds hat keinen eigenen Geschäftszweck." />
            <Field label="Angelegt" value={zeitpunkt(instrument.created)} mono />
            <Field label="Geändert" mono
                   value={instrument.updated ? zeitpunkt(instrument.updated) : undefined}
                   help="Bleibt leer, solange der Datensatz seit der Anlage nicht verändert wurde." />
          </DetailSection>

          {(instrument.market_cap_class || instrument.market_cap) && (
            <DetailSection title="Größe" span={12} columns={2}>
              <Field label="Größenklasse" value={instrument.market_cap_class ?? undefined}
                     help="Von Mega Cap (über 200 Mrd) bis Nano Cap (unter 50 Mio). Sie ist über Börsen und Währungen hinweg vergleichbar — dafür ist sie da." />
              <Field label="Marktkapitalisierung" mono
                     value={instrument.market_cap
                       ? kapitalisierung(instrument.market_cap, instrument.market_cap_ccy) : undefined}
                     help="Anzahl der Aktien mal Kurs, in der Währung des Handelsplatzes. Nicht zu verwechseln mit dem gleichnamigen Wert in den Kennzahlen: Der steht in der BILANZwährung." />
              {instrument.market_cap_ccy !== 'USD' && (
                <Field label="davon in Dollar" mono
                       value={kapitalisierung(instrument.market_cap_usd, 'USD')}
                       help="Derselbe Betrag, zum Kurs des Abruftags umgerechnet. Er dient dem Vergleich zwischen Titeln verschiedener Börsen — gemessen ist der Wert daneben." />
              )}
              <Field label="Stand" mono
                     value={instrument.market_cap_on ? datum.format(new Date(instrument.market_cap_on)) : undefined}
                     help="Tag, an dem der Betrag galt. Er ändert sich mit jedem Kurs — ein Wert von vor Monaten sagt wenig." />
            </DetailSection>
          )}

          {/*
            NICHT bei ETFs und Indizes: Die haben keine Bilanz, dort wäre ein
            leerer Block die falsche Aussage „noch nicht abgerufen". Der
            jüngste Stand als Felder, wie die Vorlage es zeigt — die Zeile
            je Abrufstand steht in den Fundamentaldaten des Imports.
          */}
          {instrument.instrument_type !== 'E' && instrument.instrument_type !== 'I' && (
            <DetailSection title="Kennzahlen" span={12} columns={2}
                           meta={stand ? `Stand ${datum.format(new Date(stand.fetched_on))} · ${stand.source}` : undefined}
                           help="Rollierende Zwölfmonatswerte (TTM) in der Bilanzwährung, zum Tag des Abrufs. Nicht das Geschäftsjahr — so bleiben Unternehmen mit verschobenem Fiskaljahr vergleichbar.">
              {stand ? <Kennzahlen stand={stand} /> : (
                <Field label="Geschäftszahlen" wide
                       value={<span className={s.stumm}>Zu diesem Titel wurden noch keine Geschäftszahlen abgerufen.</span>} />
              )}
            </DetailSection>
          )}

          <Card title="Handelsplätze" span={12} flush
                meta={`${instrument.venues.length} ${instrument.venues.length === 1 ? 'Handelsplatz' : 'Handelsplätze'}`}
                help="Je Platz das Kürzel, unter dem der Titel dort gehandelt wird, und der jüngste abgelesene Kurs in der Währung des Platzes. Der führende Platz steht oben.">
            {instrument.venues.length === 0
              ? <EmptyState title="kein Handelsplatz">Zu diesem Titel ist kein Handelsplatz eingetragen.</EmptyState>
              : <div className={s.plaetze}>
                  <div className={s.plaetzeKopf}>
                    <span>Börse</span><span>MIC</span><span>Symbol</span><span>Währung</span>
                    <span className={s.rechtsbuendig}>Letzter Kurs</span>
                  </div>
                  {instrument.venues.map((v, i) => {
                    const q = umfeld.kurse[v.exchange]
                    return (
                      <div key={v.exchange} className={s.platz} data-zebra={i % 2 === 1 || undefined}>
                        <span className={s.platzName}>{umfeld.boersen[v.exchange] ?? v.exchange}
                          {v.is_primary && <span className={s.primaer}>primär</span>}</span>
                        <span className={s.mono}>{v.exchange}</span>
                        <span className={s.mono}>{v.symbol ? v.symbol.replace(/^[A-Z0-9]{4}:/, '') : <span className={s.stumm}>—</span>}</span>
                        <span className={s.mono}>{q ? q.price.currency : <span className={s.stumm}>—</span>}</span>
                        <span className={`${s.mono} ${s.rechtsbuendig}`} title={q ? zeitpunkt(q.observed_at) : undefined}>
                          {q ? `${geldtext(q.price).replace(/ [A-Z]{3}$/, '')} · ${datum.format(new Date(q.observed_at))}`
                             : <span className={s.stumm}>kein Kurs</span>}
                        </span>
                      </div>
                    )
                  })}
                </div>}
          </Card>
        </div>

        <div className={s.rechts}>
          <AktivitaetKarte titel="FÄLLE" anzahl={umfeld.faelleGesamt} zeilen={zeilen.faelle}
                           alle={`/research?isin=${instrument.isin}`} aktiv={offen?.key ?? null}
                           leer={laedt ? 'lädt …' : 'Keine Fälle zu diesem Titel.'} onWaehle={setOffen} />
          <AktivitaetKarte titel="NEWS" anzahl={umfeld.newsGesamt} zeilen={zeilen.news}
                           alle={`/news?ref=${ref}`} aktiv={offen?.key ?? null}
                           leer={laedt ? 'lädt …' : 'Keine Meldungen.'} onWaehle={setOffen} />
          <AktivitaetKarte titel="EINSCHÄTZUNGEN" anzahl={umfeld.ampelnGesamt} zeilen={zeilen.ampeln}
                           alle={`/assessments?ref=${ref}&von=-&bis=-`} aktiv={offen?.key ?? null}
                           leer={laedt ? 'lädt …' : 'Keine Einschätzungen.'} onWaehle={setOffen} />
          <AktivitaetKarte titel="HANDELSIDEEN" anzahl={umfeld.ideenGesamt} zeilen={zeilen.ideen}
                           alle={`/trade-ideas?q=${encodeURIComponent(instrument.isin)}&beendete=1`}
                           aktiv={offen?.key ?? null}
                           leer={laedt ? 'lädt …' : 'Keine Ideen.'} onWaehle={setOffen} />
        </div>
      </div>

      {detail && (
        <AktivitaetDetail detail={detail} laedt={detail.art === 'fall' && fallLaedt} onZu={zuklappen} />
      )}
    </div>
  )
}

/** Der jüngste Kennzahlenstand als Felder. */
function Kennzahlen({ stand }: { stand: Fundamental }) {
  return (
    <>
      <Field label="Umsatz" mono value={<Geldwert wert={stand.revenue_ttm} />}
             help="Umsatz der letzten zwölf Monate, rollierend." />
      <Field label="Wachstum" mono value={<Anteil wert={stand.revenue_growth_ttm} />}
             help="Umsatzveränderung gegenüber dem Vorjahreszeitraum." />
      <Field label="Gewinn" mono value={<Geldwert wert={stand.net_income_ttm} />}
             help="Nettogewinn der letzten zwölf Monate. Negativ bei Verlust." />
      <Field label="Freier Cashflow" mono value={<Geldwert wert={stand.free_cash_flow_ttm} />}
             help="Was nach Investitionen an Zahlungsmitteln übrig bleibt. Schwerer zu gestalten als der Gewinn." />
      <Field label="FCF-Marge" mono value={<Anteil wert={stand.fcf_margin_ttm} />}
             help="Freier Cashflow im Verhältnis zum Umsatz. Bei sehr kleinen Umsätzen wird sie extrem und sagt wenig." />
      <Field label="Marktkap. (Bilanz)" mono value={<Geldwert wert={stand.market_cap} />}
             help={'Börsenwert zum Abrufstand, in der Bilanzwährung. Nicht derselbe Wert wie unter „Größe“: Der gehört zu den Stammdaten.'} />
    </>
  )
}

/** Code und Name nebeneinander: `KY · Kaimaninseln`. */
function Land({ code }: { code: string }) {
  const name = landZuCode(code)
  if (!code) return <span className={s.stumm}>—</span>
  return <>{code}{name && <span className={s.landName}>· {name}</span>}</>
}
